export * from './ts-types';

// Layout Components
export { default as ContentSection } from './components/layout/ContentSection';
export { default as Margin } from './components/layout/Margin';
export { default as Spacer } from './components/layout/Spacer';

export * from './theme';
export * from './hooks';

// Utility functions
export {
  camelCaseAriaAttrs,
  kebabCaseAriaAttrs,
} from './utils/formatAriaAttrKeys';
export { default as hashString } from './utils/hashString';
export {
  default as createUniqueClassName,
} from './utils/createUniqueClassName';

import { Img, ImgProps as _ImgProps } from './components/display/Img';
export type ImgProps = _ImgProps;
export { Img };

export { default as Link } from './components/navigation/Link';

export {
  default as DesignSystemProvider,
  useDesignSystem,
} from './components/context/DesignSystemProvider';

import { SyntheticEvent } from 'react';

export declare const Icon: React.FC<any>;
export declare const IconButton: React.FC<any>;
export declare const Text: React.FC<any>;
export declare const Dropdown: React.FC<any>;
export declare const Checkbox: React.FC<any>;
export declare const Radio: React.FC<any>;
export declare const FormLabel: React.FC<any>;
export declare const Button: React.FC<any>;
export declare const Field: React.FC<any>;
export declare const TextInput: React.FC<any>;
export declare const FloatingBox: React.FC<any>;
export declare const Menu: React.FC<any>;
export declare const MenuItem: React.FC<any>;
export declare const Nav: React.FC<any>;
export declare const Input: React.FC<any>;
export declare const Spinner: React.FC<any>;
export declare const Surface: React.FC<any>;

export declare type InputEvent = SyntheticEvent<Element>;

export declare type OnDropdownBlurMetadata = {
  /**
   * Did the blur occur within the dropdown control?
   * i.e. is some child of the Dropdown still focused?
   */
  didBlurWithin: boolean;
  /**
   * Close the menu
   */
  closeMenu: () => void;
};

export declare type FloatingMenuItem = any;
export declare type FloatingMenuGetLabelForItem = (
  item: FloatingMenuItem
) => string;

export declare type FloatingMenuProps = {
  /**
   * Accessibility label that describes the options available inside the select
   * menu, e.g. "List of items", "List of Countries", etc.
   */
  ariaLabel: string;
  /**
   * Callback that should be passed to the menu root element. Provided by
   * Downshift.
   */
  getMenuProps: (options: { 'aria-label': string }) => any;
  /**
   * Callback that should be passed to each menu item. Provided by Downshift.
   */
  getItemProps: (options: { item: FloatingMenuItem }) => any;
  /**
   * Callback that will be used to generate the react `key` to be used for each
   * item on the menu list.
   */
  getKeyForItem?: (item: FloatingMenuItem) => string;
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: FloatingMenuGetLabelForItem) => string;
  /**
   * The menu item index that should be highlighted. Provided by Downshift.
   */
  highlightedIndex: number;
  /**
   * The list of items to display in the menu.
   */
  items: Array<FloatingMenuItem>;
  /**
   * A render prop which receives a menu `item` and the `getLabelForItem`
   * function and returns a React element, which is an icon illustrating the
   * given menu item. The natural size for the icon is 30x30px. If not
   * provided, no icons will be rendered.
   */
  renderIcon?: (
    item: FloatingMenuItem,
    getLabelForItem: FloatingMenuGetLabelForItem
  ) => React.ReactNode;
};

export declare interface FloatingMenuRenderProps extends FloatingMenuProps {
  /**
   * Action to manually select an item
   */
  selectItem: (item: FloatingMenuItem) => void;
  /**
   * The currently selected item (if any)
   */
  selectedItem: FloatingMenuItem;
  /**
   * Set the currently highlighted item index
   */
  setHighlightedIndex: (index: number) => void;
  /**
   * Clear the selection
   */
  clearSelection: () => void;
  /**
   * Close the menu
   */
  closeMenu: () => void;
}
export declare type IconName = string;
export declare type TypographyName = string;
export declare const iconMap: { [s: string]: string };
